import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

// ==================== JURASSIC PARK DINOSAURS ====================

// T-Rex - massive predator
function TRexModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps massif */}
      <mesh position={[0, 0.7, 0]} rotation={[0.2, 0, 0]}>
        <capsuleGeometry args={[0.4, 0.8, 8, 12]} />
        <meshStandardMaterial color="#4a5d4a" roughness={0.8} />
      </mesh>
      {/* Queue longue */}
      <mesh position={[0, 0.5, -0.8]} rotation={[-0.3, 0, 0]}>
        <coneGeometry args={[0.25, 1.2, 8]} />
        <meshStandardMaterial color="#3d4d3d" roughness={0.8} />
      </mesh>
      {/* Cou */}
      <mesh position={[0, 0.9, 0.4]} rotation={[-0.5, 0, 0]}>
        <capsuleGeometry args={[0.15, 0.4, 6, 8]} />
        <meshStandardMaterial color="#4a5d4a" roughness={0.8} />
      </mesh>
      {/* Tête massive */}
      <mesh position={[0, 1.1, 0.7]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.35, 0.3, 0.5]} />
        <meshStandardMaterial color="#4a5d4a" roughness={0.8} />
      </mesh>
      {/* Mâchoire */}
      <mesh position={[0, 1.0, 0.85]} rotation={[0.1, 0, 0]}>
        <boxGeometry args={[0.28, 0.12, 0.35]} />
        <meshStandardMaterial color="#3d4d3d" roughness={0.8} />
      </mesh>
      {/* Dents */}
      {[-0.08, 0, 0.08].map((x, i) => (
        <mesh key={`tooth-${i}`} position={[x, 0.95, 0.95]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.02, 0.08, 4]} />
          <meshStandardMaterial color="#f0f0e0" />
        </mesh>
      ))}
      {/* Yeux */}
      <mesh position={[0.15, 1.15, 0.75]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#ffcc00" emissive="#ff6600" emissiveIntensity={0.3} />
      </mesh>
      <mesh position={[-0.15, 1.15, 0.75]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#ffcc00" emissive="#ff6600" emissiveIntensity={0.3} />
      </mesh>
      {/* Petits bras */}
      <mesh position={[0.25, 0.7, 0.3]} rotation={[0.5, 0, 0.3]}>
        <capsuleGeometry args={[0.05, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5d4a" roughness={0.8} />
      </mesh>
      <mesh position={[-0.25, 0.7, 0.3]} rotation={[0.5, 0, -0.3]}>
        <capsuleGeometry args={[0.05, 0.15, 4, 6]} />
        <meshStandardMaterial color="#4a5d4a" roughness={0.8} />
      </mesh>
      {/* Pattes arrière */}
      <mesh position={[0.2, 0.3, -0.1]} rotation={[0.1, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.4, 6, 8]} />
        <meshStandardMaterial color="#3d4d3d" roughness={0.8} />
      </mesh>
      <mesh position={[-0.2, 0.3, -0.1]} rotation={[0.1, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.4, 6, 8]} />
        <meshStandardMaterial color="#3d4d3d" roughness={0.8} />
      </mesh>
      {/* Pieds */}
      <mesh position={[0.2, 0.05, 0.05]}>
        <boxGeometry args={[0.15, 0.08, 0.25]} />
        <meshStandardMaterial color="#2d3d2d" roughness={0.9} />
      </mesh>
      <mesh position={[-0.2, 0.05, 0.05]}>
        <boxGeometry args={[0.15, 0.08, 0.25]} />
        <meshStandardMaterial color="#2d3d2d" roughness={0.9} />
      </mesh>
    </group>
  );
}

// Velociraptor - agile hunter
function VelociraptorModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps agile */}
      <mesh position={[0, 0.35, 0]} rotation={[0.15, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.35, 6, 8]} />
        <meshStandardMaterial color="#8b7355" roughness={0.7} />
      </mesh>
      {/* Queue rigide */}
      <mesh position={[0, 0.35, -0.45]} rotation={[-0.1, 0, 0]}>
        <coneGeometry args={[0.08, 0.6, 6]} />
        <meshStandardMaterial color="#7a6348" roughness={0.7} />
      </mesh>
      {/* Cou fin */}
      <mesh position={[0, 0.45, 0.2]} rotation={[-0.6, 0, 0]}>
        <capsuleGeometry args={[0.05, 0.2, 4, 6]} />
        <meshStandardMaterial color="#8b7355" roughness={0.7} />
      </mesh>
      {/* Tête allongée */}
      <mesh position={[0, 0.55, 0.35]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.1, 0.08, 0.2]} />
        <meshStandardMaterial color="#8b7355" roughness={0.7} />
      </mesh>
      {/* Yeux intelligents */}
      <mesh position={[0.045, 0.57, 0.38]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ffaa00" emissive="#ff6600" emissiveIntensity={0.4} />
      </mesh>
      <mesh position={[-0.045, 0.57, 0.38]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ffaa00" emissive="#ff6600" emissiveIntensity={0.4} />
      </mesh>
      {/* Bras avec griffes */}
      <mesh position={[0.12, 0.35, 0.15]} rotation={[0.8, 0, 0.4]}>
        <capsuleGeometry args={[0.03, 0.12, 4, 6]} />
        <meshStandardMaterial color="#8b7355" roughness={0.7} />
      </mesh>
      <mesh position={[-0.12, 0.35, 0.15]} rotation={[0.8, 0, -0.4]}>
        <capsuleGeometry args={[0.03, 0.12, 4, 6]} />
        <meshStandardMaterial color="#8b7355" roughness={0.7} />
      </mesh>
      {/* Pattes */}
      <mesh position={[0.08, 0.15, 0]} rotation={[0.1, 0, 0]}>
        <capsuleGeometry args={[0.04, 0.2, 4, 6]} />
        <meshStandardMaterial color="#7a6348" roughness={0.7} />
      </mesh>
      <mesh position={[-0.08, 0.15, 0]} rotation={[0.1, 0, 0]}>
        <capsuleGeometry args={[0.04, 0.2, 4, 6]} />
        <meshStandardMaterial color="#7a6348" roughness={0.7} />
      </mesh>
      {/* Griffes célèbres */}
      <mesh position={[0.08, 0.08, 0.08]} rotation={[0.8, 0, 0]}>
        <coneGeometry args={[0.015, 0.08, 4]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[-0.08, 0.08, 0.08]} rotation={[0.8, 0, 0]}>
        <coneGeometry args={[0.015, 0.08, 4]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
    </group>
  );
}

// Triceratops - three-horned herbivore
function TriceratopsModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps massif */}
      <mesh position={[0, 0.4, 0]}>
        <capsuleGeometry args={[0.3, 0.5, 8, 10]} />
        <meshStandardMaterial color="#8b8b7a" roughness={0.8} />
      </mesh>
      {/* Collerette */}
      <mesh position={[0, 0.55, 0.35]} rotation={[0.3, 0, 0]}>
        <cylinderGeometry args={[0.35, 0.25, 0.08, 8]} />
        <meshStandardMaterial color="#7a7a6a" roughness={0.8} />
      </mesh>
      {/* Tête */}
      <mesh position={[0, 0.45, 0.5]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.25, 0.2, 0.25]} />
        <meshStandardMaterial color="#8b8b7a" roughness={0.8} />
      </mesh>
      {/* Bec */}
      <mesh position={[0, 0.42, 0.65]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.08, 0.12, 4]} />
        <meshStandardMaterial color="#6a6a5a" roughness={0.9} />
      </mesh>
      {/* Cornes frontales */}
      <mesh position={[0.1, 0.55, 0.55]} rotation={[-0.5, 0.2, 0]}>
        <coneGeometry args={[0.03, 0.25, 6]} />
        <meshStandardMaterial color="#f0e8d0" />
      </mesh>
      <mesh position={[-0.1, 0.55, 0.55]} rotation={[-0.5, -0.2, 0]}>
        <coneGeometry args={[0.03, 0.25, 6]} />
        <meshStandardMaterial color="#f0e8d0" />
      </mesh>
      {/* Corne nasale */}
      <mesh position={[0, 0.48, 0.62]} rotation={[-0.3, 0, 0]}>
        <coneGeometry args={[0.025, 0.1, 6]} />
        <meshStandardMaterial color="#f0e8d0" />
      </mesh>
      {/* Yeux */}
      <mesh position={[0.12, 0.48, 0.5]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color="#4a3a2a" />
      </mesh>
      <mesh position={[-0.12, 0.48, 0.5]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color="#4a3a2a" />
      </mesh>
      {/* Pattes */}
      {[[0.18, 0.2], [-0.18, 0.2], [0.15, -0.15], [-0.15, -0.15]].map(([x, z], i) => (
        <mesh key={`leg-${i}`} position={[x, 0.15, z]}>
          <cylinderGeometry args={[0.08, 0.1, 0.3, 6]} />
          <meshStandardMaterial color="#7a7a6a" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

// Brachiosaurus - long-necked giant
function BrachiosaurusModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps énorme */}
      <mesh position={[0, 0.6, 0]}>
        <capsuleGeometry args={[0.35, 0.6, 8, 10]} />
        <meshStandardMaterial color="#6b8e6b" roughness={0.7} />
      </mesh>
      {/* Long cou */}
      <mesh position={[0, 1.1, 0.3]} rotation={[-0.4, 0, 0]}>
        <capsuleGeometry args={[0.1, 0.8, 6, 8]} />
        <meshStandardMaterial color="#5a7d5a" roughness={0.7} />
      </mesh>
      {/* Petite tête */}
      <mesh position={[0, 1.6, 0.6]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial color="#6b8e6b" roughness={0.7} />
      </mesh>
      {/* Yeux doux */}
      <mesh position={[0.08, 1.62, 0.68]}>
        <sphereGeometry args={[0.025, 6, 6]} />
        <meshStandardMaterial color="#3a2a1a" />
      </mesh>
      <mesh position={[-0.08, 1.62, 0.68]}>
        <sphereGeometry args={[0.025, 6, 6]} />
        <meshStandardMaterial color="#3a2a1a" />
      </mesh>
      {/* Queue */}
      <mesh position={[0, 0.5, -0.5]} rotation={[0.2, 0, 0]}>
        <coneGeometry args={[0.2, 0.8, 6]} />
        <meshStandardMaterial color="#5a7d5a" roughness={0.7} />
      </mesh>
      {/* Pattes massives */}
      {[[0.2, 0.2], [-0.2, 0.2], [0.18, -0.2], [-0.18, -0.2]].map(([x, z], i) => (
        <mesh key={`leg-${i}`} position={[x, 0.2, z]}>
          <cylinderGeometry args={[0.1, 0.12, 0.4, 6]} />
          <meshStandardMaterial color="#4a6d4a" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

// Pterodactyl - flying reptile
function PterodactylModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps */}
      <mesh position={[0, 0.3, 0]}>
        <capsuleGeometry args={[0.08, 0.2, 6, 8]} />
        <meshStandardMaterial color="#a08060" roughness={0.6} />
      </mesh>
      {/* Tête avec crête */}
      <mesh position={[0, 0.35, 0.2]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.05, 0.2, 6]} />
        <meshStandardMaterial color="#a08060" roughness={0.6} />
      </mesh>
      {/* Crête */}
      <mesh position={[0, 0.42, 0.15]} rotation={[-0.5, 0, 0]}>
        <coneGeometry args={[0.02, 0.15, 4]} />
        <meshStandardMaterial color="#c0a080" roughness={0.6} />
      </mesh>
      {/* Ailes membraneuses */}
      <mesh position={[0.4, 0.32, 0]} rotation={[0, 0, 0.2]} scale={[1, 0.02, 0.4]}>
        <boxGeometry args={[0.6, 1, 1]} />
        <meshStandardMaterial color="#d0b090" transparent opacity={0.8} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[-0.4, 0.32, 0]} rotation={[0, 0, -0.2]} scale={[1, 0.02, 0.4]}>
        <boxGeometry args={[0.6, 1, 1]} />
        <meshStandardMaterial color="#d0b090" transparent opacity={0.8} side={THREE.DoubleSide} />
      </mesh>
      {/* Yeux */}
      <mesh position={[0.03, 0.36, 0.25]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#ffcc00" />
      </mesh>
      <mesh position={[-0.03, 0.36, 0.25]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#ffcc00" />
      </mesh>
    </group>
  );
}

// Registry export
export const jurassicParkNPCs: NPCModelRegistry = {
  t_rex: TRexModel,
  velociraptor: VelociraptorModel,
  triceratops: TriceratopsModel,
  brachiosaurus: BrachiosaurusModel,
  pterodactyl: PterodactylModel,
};
